import React, { useState, useRef } from 'react';
import './PatternGeneratorText.css';

interface PatternSection {
  cubeGrid: number[][];
  textDescription: string;
}

const PatternGeneratorText: React.FC = () => {
  const [sections, setSections] = useState<PatternSection[]>([
    { cubeGrid: [], textDescription: '' },
    { cubeGrid: [], textDescription: '' },
    { cubeGrid: [], textDescription: '' },
    { cubeGrid: [], textDescription: '' }
  ]);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const GRID_SIZE = 20;
  const SECTION_WIDTH = 300;
  const SECTION_HEIGHT = 300;

  const parseTextToCubeGrid = (text: string) => {
    const lines = text.trim().split('\n');
    const cubeGrid: number[][] = [];
    
    lines.forEach((line) => {
      const numbers = line.trim().split(/\s+/).map(num => parseInt(num, 10));
      // Filter out NaN values and ensure positive integers
      const validNumbers = numbers.map(num => isNaN(num) || num < 0 ? 0 : num);
      if (validNumbers.length > 0) {
        cubeGrid.push(validNumbers);
      }
    });
    
    return cubeGrid;
  };

  const handleTextChange = (sectionIndex: number, text: string) => {
    setSections(prev => prev.map((section, index) => {
      if (index === sectionIndex) {
        const newCubeGrid = parseTextToCubeGrid(text);
        return {
          ...section,
          textDescription: text,
          cubeGrid: newCubeGrid
        };
      }
      return section;
    }));
  };


  const copySection = (fromIndex: number, toIndex: number) => {
    setSections(prev => prev.map((section, index) => 
      index === toIndex 
        ? { 
            cubeGrid: [...prev[fromIndex].cubeGrid.map(row => [...row])],
            textDescription: prev[fromIndex].textDescription
          }
        : section
    ));
  };

  const clearSection = (sectionIndex: number) => {
    setSections(prev => prev.map((section, index) => 
      index === sectionIndex 
        ? { cubeGrid: [], textDescription: '' }
        : section
    ));
  };

  // Cube rendering functions (adapted from CubeRenderer)
  const renderCube = (row: number, col: number, z: number, size: number) => {
    const cubeSize = 20; // Smaller cubes for the pattern generator
    const centerX = size / 2;
    const centerY = size / 2;
    
    const angleX = Math.PI / 6.5;
    const angleY = Math.PI / 7.5;
    const w = cubeSize * Math.cos(angleX);
    const h = cubeSize * Math.sin(angleY);
    
    const isoX = (col - row) * w;
    const isoY = (col + row) * h - z * cubeSize;
    
    const x = centerX + isoX;
    const y = centerY + isoY;
    
    return (
      <g key={`${row}-${col}-${z}`}>
        {/* Top face */}
        <polygon
          points={`
            ${x},${y}
            ${x + w},${y + h}
            ${x},${y + 2 * h}
            ${x - w},${y + h}
          `}
          fill="#ffffff"
          stroke="#000000"
          strokeWidth="1"
          strokeLinejoin="round"
        />
        
        {/* Right face */}
        <polygon
          points={`
            ${x},${y + 2 * h}
            ${x + w},${y + h}
            ${x + w},${y + h + cubeSize}
            ${x},${y + 2 * h + cubeSize}
          `}
          fill="#d8d8d8"
          stroke="#000000"
          strokeWidth="1"
          strokeLinejoin="round"
        />
        
        {/* Left face */}
        <polygon
          points={`
            ${x - w},${y + h}
            ${x},${y + 2 * h}
            ${x},${y + 2 * h + cubeSize}
            ${x - w},${y + h + cubeSize}
          `}
          fill="#b0b0b0"
          stroke="#000000"
          strokeWidth="1"
          strokeLinejoin="round"
        />
      </g>
    );
  };

  const renderCubeGrid = (grid: number[][], sectionSize: number) => {
    if (!grid || grid.length === 0) return [];
    
    const cubes: React.ReactElement[] = [];
    const positions: { row: number; col: number; z: number }[] = [];
    
    // Collect all cube positions
    for (let row = 0; row < grid.length; row++) {
      for (let col = 0; col < grid[row].length; col++) {
        const height = grid[row][col];
        for (let z = 0; z < height; z++) {
          positions.push({ row, col, z });
        }
      }
    }
    
    // Sort for proper rendering order
    positions.sort((a, b) => {
      const sumA = a.row + a.col;
      const sumB = b.row + b.col;
      if (sumA !== sumB) return sumA - sumB;
      
      if (a.row !== b.row) return a.row - b.row;
      if (a.col !== b.col) return b.col - a.col;
      return a.z - b.z;
    });
    
    // Render each cube
    positions.forEach(pos => {
      cubes.push(renderCube(pos.row, pos.col, pos.z, sectionSize));
    });
    
    return cubes;
  };

  const exportToPNG = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const CLUSTER_GAP = 150; // Much larger gap between clusters
    const MARGIN = 80;
    const CUBE_AREA_SIZE = 250; // Slightly smaller area to ensure separation
    
    // Calculate total width
    let totalWidth = MARGIN * 2 + (CUBE_AREA_SIZE * 4) + (CLUSTER_GAP * 3);
    const totalHeight = CUBE_AREA_SIZE + MARGIN * 2;

    canvas.width = totalWidth;
    canvas.height = totalHeight;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, totalWidth, totalHeight);

    let currentX = MARGIN;
    
    sections.forEach((section, sectionIndex) => {
      // Optional: Draw light boundary for debugging (comment out for production)
      // ctx.strokeStyle = '#e0e0e0';
      // ctx.lineWidth = 1;
      // ctx.strokeRect(currentX, MARGIN, CUBE_AREA_SIZE, CUBE_AREA_SIZE);
      
      if (section.cubeGrid.length > 0) {
        // Render cubes using canvas drawing
        renderCubesOnCanvas(ctx, section.cubeGrid, currentX, MARGIN, CUBE_AREA_SIZE);
      } else if (sectionIndex === 3) {
        // Fourth section is empty - draw question mark
        const questionMarkX = currentX + CUBE_AREA_SIZE / 2;
        const questionMarkY = MARGIN + CUBE_AREA_SIZE / 2;
        
        ctx.fillStyle = '#666';
        ctx.font = 'bold 100px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('?', questionMarkX, questionMarkY);
      }
      
      currentX += CUBE_AREA_SIZE;
      if (sectionIndex < 3) {
        currentX += CLUSTER_GAP;
      }
    });

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cube-pattern-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  const renderCubesOnCanvas = (ctx: CanvasRenderingContext2D, grid: number[][], offsetX: number, offsetY: number, areaSize: number) => {
    const cubeSize = 25; // Balanced size to fit within area boundaries
    const centerX = offsetX + areaSize / 2;
    const centerY = offsetY + areaSize / 2;
    
    const angleX = Math.PI / 6.5;
    const angleY = Math.PI / 7.5;
    const w = cubeSize * Math.cos(angleX);
    const h = cubeSize * Math.sin(angleY);
    
    const positions: { row: number; col: number; z: number }[] = [];
    
    // Collect all cube positions
    for (let row = 0; row < grid.length; row++) {
      for (let col = 0; col < grid[row].length; col++) {
        const height = grid[row][col];
        for (let z = 0; z < height; z++) {
          positions.push({ row, col, z });
        }
      }
    }
    
    // Sort for proper rendering order
    positions.sort((a, b) => {
      const sumA = a.row + a.col;
      const sumB = b.row + b.col;
      if (sumA !== sumB) return sumA - sumB;
      
      if (a.row !== b.row) return a.row - b.row;
      if (a.col !== b.col) return b.col - a.col;
      return a.z - b.z;
    });
    
    // Render each cube
    positions.forEach(pos => {
      const isoX = (pos.col - pos.row) * w;
      const isoY = (pos.col + pos.row) * h - pos.z * cubeSize;
      
      const x = centerX + isoX;
      const y = centerY + isoY;
      
      // Top face
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + w, y + h);
      ctx.lineTo(x, y + 2 * h);
      ctx.lineTo(x - w, y + h);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      
      // Right face
      ctx.fillStyle = '#d8d8d8';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x, y + 2 * h);
      ctx.lineTo(x + w, y + h);
      ctx.lineTo(x + w, y + h + cubeSize);
      ctx.lineTo(x, y + 2 * h + cubeSize);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      
      // Left face
      ctx.fillStyle = '#b0b0b0';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x - w, y + h);
      ctx.lineTo(x, y + 2 * h);
      ctx.lineTo(x, y + 2 * h + cubeSize);
      ctx.lineTo(x - w, y + h + cubeSize);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    });
  };

  return (
    <div className="pattern-generator-text">
      <h2>Cube Pattern Generator (Text Input)</h2>
      
      {/* Pattern Sections */}
      <div className="pattern-sections">
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="section-container">
            <div className="pattern-section">
              <svg 
                width={SECTION_WIDTH} 
                height={SECTION_HEIGHT} 
                viewBox={`0 0 ${SECTION_WIDTH} ${SECTION_HEIGHT}`}
                style={{ backgroundColor: '#fafafa', border: '2px solid #333' }}
              >
                {renderCubeGrid(section.cubeGrid, SECTION_WIDTH)}
              </svg>
            </div>
            
            <div className="section-controls">
              <button onClick={() => clearSection(sectionIndex)}>Clear</button>
              {sectionIndex < 3 && (
                <button onClick={() => copySection(sectionIndex, sectionIndex + 1)}>
                  Copy →
                </button>
              )}
            </div>

            {/* Text Input Box */}
            <div className="text-input-container">
              <textarea
                className="pattern-text-input"
                placeholder="Enter cube heights like:&#10;3 3 0&#10;3 2 1&#10;1 1 0"
                value={section.textDescription}
                onChange={(e) => handleTextChange(sectionIndex, e.target.value)}
                rows={4}
                cols={10}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Export Button */}
      <div className="export-controls">
        <button className="export-btn" onClick={exportToPNG}>
          Export as PNG
        </button>
      </div>

      {/* Hidden canvas for export */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default PatternGeneratorText;